import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import api from "../api";
import { Link } from "react-router-dom";
import { Search, ChevronUp, ChevronDown } from "lucide-react";
import Pagination from '../components/Pagination';
 
function SentToPaymentList({ userRole }) {
  const { instance, accounts } = useMsal();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expenseType, setExpenseType] = useState('all');
  const [sortDirection, setSortDirection] = useState('asc');
 
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
   const userRoles = typeof userRole === 'string' ? userRole.split(',').map(r => r.trim()) : [userRole || 'creator'];
  const hasRole = (role) => userRoles.includes(role);

  const isKishor = accounts?.[0]?.username?.toLowerCase() === 'kishor.dholwani@acornuniversalconsultancy.com';

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Acquire MSAL token for protected backend route
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: accounts[0]
        });

        const res = await api.get(
          "/api/payments/all-expense-details",
          { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
        );

        // Start from backend list (all expense details)
        let base = Array.isArray(res.data) ? [...res.data] : [];

        // Sort descending: latest entered on top
        const sorted = [...base].sort((a, b) => {
          const ta = a?.date ? new Date(a.date).getTime() : (a?.created_at ? new Date(a.created_at).getTime() : 0);
          const tb = b?.date ? new Date(b.date).getTime() : (b?.created_at ? new Date(b.created_at).getTime() : 0);
          if (tb !== ta) return tb - ta;
          // Fallback: numeric suffix of expense_id
          const numA = parseInt((a?.expense_id || '').match(/(\d+)$/)?.[1] || '0', 10);
          const numB = parseInt((b?.expense_id || '').match(/(\d+)$/)?.[1] || '0', 10);
          return numB - numA;
        });

        // Backfill expense_name for display
        try {
          const ids = Array.from(new Set(sorted.map(r => String(r.expense_id || '')))).filter(Boolean);
          const nameFetches = ids.map(async (id) => {
            try {
              const info = await api.get('/api/expenses/by', {
                params: { expenseId: id },
                headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
              });
              return [id, info?.data?.expense_name || null];
            } catch (_) {
              return [id, null];
            }
          });
          const pairs = await Promise.all(nameFetches);
          const nameMap = new Map(pairs);
          const withNames = sorted.map(r => ({ ...r, expense_name: nameMap.get(String(r.expense_id)) || r.expense_name || null }));
          setRecords(withNames);
          setFilteredRecords(withNames);
        } catch (e) {
          console.warn('Failed to backfill expense names for adjustment list:', e);
          setRecords(sorted);
          setFilteredRecords(sorted);
        }
        setLoading(false);
      } catch (err) {
        console.error("SentToPayment fetch error:", err);
        setError(
          err?.response?.status === 401
            ? "Unauthorized. Please sign in again."
            : "Failed to fetch data."
        );
        setLoading(false);
      }
    };

    if (accounts && accounts.length > 0) {
      fetchData();
    }
  }, [accounts, instance]);
 
  const derivedFiltered = useMemo(() => {
    let list = [...filteredRecords];
    // Search by expense id or vendor
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(r => (r.expense_id || '').toLowerCase().includes(q) || (r.vendor_name || '').toLowerCase().includes(q) || (r.expense_name || '').toLowerCase().includes(q));
    }
    // Type filter (IT/Admin/Accounts) derived from expense_id
    if (expenseType !== 'all') {
      list = list.filter(r => {
        const id = (r.expense_id || '').toUpperCase();
        if (expenseType === 'IT') return id.includes('/IT/');
        if (expenseType === 'Accounts') return id.includes('/ACCOUNT/');
        if (expenseType === 'Admin') return !id.includes('/IT/') && !id.includes('/ACCOUNT/');
        return true;
      });
    }
    // Sort by Expense ID
    list.sort((a, b) => {
      const av = String(a.expense_id || '');
      const bv = String(b.expense_id || '');
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredRecords, searchTerm, expenseType, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(derivedFiltered.length / itemsPerPage));
  const paginatedRecords = derivedFiltered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleSort = () => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');

  if (loading) return <div className="p-6" style={{ color: 'var(--muted)' }}>Loading...</div>;
  if (error) return <div className="p-6" style={{ color: '#dc2626' }}>{error}</div>;

  return (
    <div className="p-6 w-full space-y-6" style={{ color: 'var(--text)' }}>
      <div>
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--heading-color, var(--text))' }}>Payment Adjustment</h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>Manage adjusted payments and reconcile final approved values</p>
      </div>

      <div className="rounded-xl shadow-sm border p-4" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
        <p className="text-sm mb-2" style={{ color: 'var(--muted)' }}>
          {derivedFiltered.length} records found
        </p>

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="relative w-full md:w-1/3">
            <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search by Expense ID or Vendor..."
              className="w-full pl-10 pr-4 py-2 text-sm rounded-md shadow-sm border"
              style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm" style={{ color: 'var(--muted)' }}>Type</label>
            <select
              value={expenseType}
              onChange={(e) => { setExpenseType(e.target.value); setCurrentPage(1); }}
              className="border px-3 py-2 rounded-md shadow-sm"
              style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
            >
              <option value="all">All Types</option>
              <option value="IT">IT Expenses</option>
              <option value="Admin">Admin Expenses</option>
              <option value="Accounts">Accounts Expenses</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left border-collapse" style={{ color: 'var(--text)' }}>
            <thead className="border-b" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
              <tr>
                <th className="p-2 cursor-pointer" onClick={toggleSort} title="Sort by Expense ID">
                  <div className="flex items-center gap-1">
                    Expense ID
                    {sortDirection === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </div>
                </th>
                <th className="p-2">Expense Name</th>
                <th className="p-2" >Vendor</th>
                <th className="p-2" >Approved Amount</th>
                <th className="p-2" >Date</th>
                <th className="p-2" >Actual Amount</th>
                <th className="p-2" >View Attachments</th>
                {/* <th className="p-2">Action</th> */}
              </tr>
            </thead>
            <tbody className="text-sm">
              {paginatedRecords.map((rec) => (
                <tr key={rec.id} className="border-b" style={{ borderColor: 'var(--border)' }}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isKishor ? (
                      ['Approved', 'Rejected'].includes(rec.expense_status) ? (
                        <div className="flex items-center gap-2">
                          <span className={rec.expense_status === 'Approved' ? "text-green-700 text-sm font-medium" : "text-red-700 text-sm font-medium"}>
                            {rec.expense_id}
                          </span>
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                            rec.expense_status === 'Approved' 
                              ? "bg-green-50 text-green-700 border-green-200" 
                              : "bg-red-50 text-red-700 border-red-200"
                          }`}>
                            {rec.expense_status}
                          </span>
                        </div>
                      ) : rec.actual_paid ? (
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/l3-approval/${encodeURIComponent(rec.expense_id)}/${rec.id}`}
                            className="text-blue-600 text-sm font-medium underline"
                          >
                            {rec.expense_id}
                          </Link>
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200">
                            Requires L3 Approval
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 text-sm">{rec.expense_id}</span>
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                            Awaiting Update
                          </span>
                        </div>
                      )
                    ) : (hasRole("approver") || hasRole("payment")) ? (
                      <span style={{ color: "var(--text)" }} className="text-sm font-medium">{rec.expense_id}</span>
                    ) : (
                      <Link
                        to={`/payment-adjustment/${encodeURIComponent(rec.expense_id)}/${rec.id}`}
                        className="font-medium underline text-sm"
                        style={{ color: 'var(--accent)' }}
                      >
                        {rec.expense_id}
                      </Link>
                    )}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">{rec.expense_name || '—'}</td>
                  <td className="px-6 py-3 whitespace-nowrap">{rec.vendor_name}</td>
                  <td className="px-6 py-3 whitespace-nowrap">₹{rec.cost}</td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    {rec.date ? new Date(rec.date).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    {rec.actual_paid ? `₹${rec.actual_paid}` : "-"}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    {rec.actual_attachment_url ? (
                      <a href={rec.actual_attachment_url} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: 'var(--accent)' }}>
                        View Attachments
                      </a>
                    ) : <span style={{ color: 'var(--muted)' }}>No file</span>}
                  </td>
                  {/* <td className="px-6 py-3 whitespace-nowrap">
                    <Link
                      to={`/l3-approval/${encodeURIComponent(rec.expense_id)}`}
                      className="px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      Review
                    </Link>
                  </td> */}
                </tr>
              ))}

              {paginatedRecords.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-4" style={{ color: 'var(--muted)' }}>
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {derivedFiltered.length > 0 && (
          <div className="border-t" style={{ borderColor: 'var(--border)' }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => setCurrentPage(p)}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
              totalItems={derivedFiltered.length}
            />
          </div>
        )}
      </div>
    </div>
  );
}
 
export default SentToPaymentList;




// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { Link } from "react-router-dom";

// function SentToPaymentList() {
//   const [records, setRecords] = useState([]);
//   const [filteredRecords, setFilteredRecords] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");

//   // Pagination
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 10;

//   useEffect(() => {
//     axios
//       .get("http://localhost:5000/api/payments/sent-to-payment")
//       .then((res) => {
//         setRecords(res.data);
//         setFilteredRecords(res.data); // You can later add filters/search here
//         setLoading(false);
//       })
//       .catch((err) => {
//         setError("Failed to fetch data.");
//         setLoading(false);
//       });
//   }, []);

//   const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
//   const paginatedRecords = filteredRecords.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   if (loading) return <div className="p-6">Loading...</div>;
//   if (error) return <div className="p-6 text-red-600">{error}</div>;

//   return (
//     <div className="p-6 max-w-8xl mx-auto">
//       <h2 className="text-2xl font-bold mb-6">Sent To Payment List</h2>

//       <div className="bg-white rounded-xl shadow border p-4">
//         <p className="text-sm text-gray-500 mb-2">
//           {filteredRecords.length} records found
//         </p>

//         <div className="overflow-x-auto">
//           <table className="min-w-full text-sm text-left border-collapse">
//             <thead className="border-b text-gray-600">
//               <tr>
//                 <th className="p-2">Expense ID</th>
//                 <th className="p-2">Vendor</th>
//                 <th className="p-2">Approved Amount</th>
//                 <th className="p-2">Date</th>
//                 <th className="p-2">Actual Amount</th>
//               </tr>
//             </thead>
//             <tbody className="divide-y divide-gray-100 text-sm">
//               {paginatedRecords.map((rec) => (
//                 <tr key={rec.id} className="hover:bg-gray-50">
//                   <td className="px-6 py-3 text-blue-700 font-medium underline whitespace-nowrap">
//                     <Link
//                       to={`/payment-adjustment/${encodeURIComponent(
//                         rec.expense_id
//                       )}`}
//                     >
//                       {rec.expense_id}
//                     </Link>
//                   </td>
//                   <td className="px-6 py-3 whitespace-nowrap">{rec.vendor_name}</td>
//                   <td className="px-6 py-3 whitespace-nowrap">₹{rec.cost}</td>
//                   <td className="px-6 py-3 whitespace-nowrap">
//                     {rec.date ? new Date(rec.date).toLocaleDateString() : "-"}
//                   </td>
//                   <td className="px-6 py-3 whitespace-nowrap">
//                     {rec.actual_paid ? `₹${rec.actual_paid}` : "-"}
//                   </td>
//                 </tr>
//               ))}

//               {paginatedRecords.length === 0 && (
//                 <tr>
//                   <td colSpan={5} className="text-center py-4 text-gray-400">
//                     No records found
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>

//         {/* Pagination Controls */}
//         {filteredRecords.length > 0 && (
//           <div className="flex items-center justify-between mt-6">
//             <button
//               className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
//               onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
//               disabled={currentPage === 1}
//             >
//               Previous
//             </button>

//             <div className="flex space-x-1">
//               {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
//                 <button
//                   key={num}
//                   onClick={() => setCurrentPage(num)}
//                   className={`h-8 w-8 text-sm font-medium rounded-md ${
//                     currentPage === num
//                       ? "bg-red-800 text-white shadow-md"
//                       : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
//                   }`}
//                 >
//                   {num}
//                 </button>
//               ))}
//             </div>

//             <button
//               className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
//               onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
//               disabled={currentPage === totalPages}
//             >
//               Next
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// export default SentToPaymentList;



